// Date / time formatters (CONTRACT-BOUNDARY.md §2).
//
// Every user-facing date spells the month out, so no ordering ambiguity can
// occur in display copy. Ranges use an en dash (U+2013) with spaces, never a
// hyphen. Times are 12-hour, `AM`/`PM` uppercase, no leading zero on the hour.
//
// These are deliberately NOT the shared `timeFormat` in common/utils/date.ts:
// that helper is `Intl`-based and locale-aware (it emits 24-hour or localised
// separators in some cultures), whereas the calendar copy spec (COPY.md) fixes
// the format at `h:mm a`. `tileTime` is the single 12-hour formatter here; every
// other time string composes from it so they cannot drift.

import { format, isSameMonth, setHours, setMinutes } from 'date-fns'

// En dash with surrounding spaces — the required range separator.
export const EN_DASH = '–'

/** Day-view heading and popover date row: `Monday, March 9, 2026`. */
export function dayHeading(date: Date): string {
  return format(date, 'EEEE, MMMM d, yyyy')
}

/** Month heading: `March 2026`. */
export function monthHeading(date: Date): string {
  return format(date, 'MMMM yyyy')
}

/**
 * Week heading — three formats:
 *  - same month:      `March 8 – 14, 2026`        (`MMMM d – d, yyyy`)
 *  - spanning months: `Mar 29 – Apr 4, 2026`      (`MMM d – MMM d, yyyy`)
 *  - spanning years:  `Dec 28, 2025 – Jan 3, 2026` (both years — otherwise the
 *    start year is lost and Dec 28 reads as 2026)
 *
 * `end` is the INCLUSIVE last day of the week (Saturday), not an exclusive bound.
 */
export function weekHeading(start: Date, end: Date): string {
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'MMM d, yyyy')} ${EN_DASH} ${format(end, 'MMM d, yyyy')}`
  }
  if (isSameMonth(start, end)) {
    return `${format(start, 'MMMM d')} ${EN_DASH} ${format(end, 'd, yyyy')}`
  }
  return `${format(start, 'MMM d')} ${EN_DASH} ${format(end, 'MMM d, yyyy')}`
}

/** Tile start time: `9:30 AM`. */
export function tileTime(date: Date): string {
  return format(date, 'h:mm a')
}

/** Tile / popover time range: `9:30 AM – 10:30 AM`. */
export function timeRange(start: Date, end: Date): string {
  return `${tileTime(start)} ${EN_DASH} ${tileTime(end)}`
}

/** Hour gutter label for a 0–23 hour: `7 AM`. */
export function hourGutter(hour: number): string {
  return format(setMinutes(setHours(new Date(), hour), 0), 'h a')
}

/**
 * Day-header event count: `1 event` for exactly one, `{n} events` otherwise —
 * including `0 events` (COPY.md §3). Pluralisation is explicit, never `+ 's'`.
 */
export function eventCountLabel(count: number): string {
  return count === 1 ? '1 event' : `${count} events`
}

/** Overflow affordance copy: `1 more event` / `{n} more events`. */
export function moreEventsLabel(count: number): string {
  return count === 1 ? '1 more event' : `${count} more events`
}

/**
 * Activity detail-popover START / DUE rows (SH-1986, P2): a long, unambiguous
 * `Monday, March 9, 2026 · 9:30 AM`. Overrides the AC's MM/DD/YYYY wording by
 * team decision.
 */
export function detailDateTime(date: Date): string {
  return format(date, 'EEEE, MMMM d, yyyy · h:mm a')
}

// A Next Action / Expected Close marker has a date but no meaningful time; the
// AC pins the displayed time to 11:59 PM (P4).
const MARKER_TIME = '11:59 PM'

/**
 * Marker (NAD/ECD) detail row — the same long date format as an activity's
 * START/DUE rows, with the time pinned to 11:59 PM (matches the design handoff):
 * `Wednesday, March 11, 2026 · 11:59 PM`.
 */
export function markerDateTime(date: Date): string {
  return `${format(date, 'EEEE, MMMM d, yyyy')} · ${MARKER_TIME}`
}
